import {Brick, registerBrick, Config, ErrorFlow} from 'olympe';
import {addNBusinessDays} from '../date';
import {Material} from '../../models/Material';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {POSendingOptions} from '../../models/POSendingOptions';
import {FinancialInfo} from '../../models/FinancialInfo';
import {AttachmentsInfo} from '../../models/AttachmentsInfo';
import {createPRJson} from '../createPRJson';
import {OneTimeVendor} from '../../models/OneTimeVendor';
import {HeaderDetails} from '../../models/HeaderDetails';
import {cleanEmailList} from '../email';
import {NBSValidation} from '../../models/NBSValidation';
import {parseAccounts, parseLabelValue, parsePlants} from '../parseAPI';
import {checkIfPriceDataFromAPI, getVendorCodes} from '../helpers';
import {SBValidation} from '../../models/SBValidation';
import {extractValuesFromWayOfBuyingOptions, formatWaysOfBuying} from '../wayOfBuying';
import {WaysOfBuying} from '../../models/WaysOfBuying';
import {getStringValueForSourceFromApiKey, MAX_LENGTH_OF_MATERIAL_NAME_AND_MATERIAL_DESC, SOURCES} from '../constants';
import {complianceIndicatorOptions, getUniquePrId, wayOfBuyingOptions} from '../../components/_helpers';
import {convertSourceToLabelValue} from '../sourceMappings';

/**
 * A brick that create a PR JSON data from JSON data coming from rest_api
 */
export default class ConvertAPIPayloadToPRJson extends Brick {
    /**
     * Executed every time an input gets updated.
     * Note that this method will _not_ be executed if an input value is undefined.
     *
     * @protected
     * @param {BrickContext} $
     * @param {string} apiPayload
     * @param {{}} spenderInfo
     * @param {{}} requesterInfo
     * @param {{}} userDefaults
     * @param {function(string)} setPRJson
     * @param {function(ErrorFlow)} setErrorFlow
     */
    async update($, [apiPayload, spenderInfo, requesterInfo, userDefaults], [setPRJson, setErrorFlow]) {
        try {
            const apiData = JSON.parse(apiPayload);
            const today = new Date().toDateString();

            let uniquePrId;
            try {
                uniquePrId = await getUniquePrId($);
            } catch (error) {
                setErrorFlow(ErrorFlow.create(`Unable to create a unique ID: ${error}`, 1003));
                return;
            }

            const requestDeliveryDate = addNBusinessDays(new Date(), apiData.supplierLeadTime || 5).toDateString();
            const deliveryDate = apiData.deliveryDate ? new Date(apiData.deliveryDate).toDateString() : requestDeliveryDate;
            const userNameUppercase = apiData['username'].toUpperCase();
            const requester = apiData['requester']?.toUpperCase();
            const formattedWaysOfBuying = formatWaysOfBuying(apiData.wayOfBuying);

            if (apiData.source === SOURCES.TSF) {
                formattedWaysOfBuying.availableOptions = extractValuesFromWayOfBuyingOptions(wayOfBuyingOptions);
            }

            const hidePrice = userDefaults.hide_price !== null && formattedWaysOfBuying.currentValue.value === 'FO' ? userDefaults.hide_price : formattedWaysOfBuying.currentValue.value === 'FO';

            const [OTVCode, CMVCode, MTVCode] = getVendorCodes(Config.getParameter('ENV'));

            // Here we check if the spender & requester are in the users list. Because if the creator of the request send
            // an email, this email could not be in our DB.
            if (!spenderInfo) {
                spenderInfo = {
                    id: apiData['username'],
                    description: apiData['username'],
                    email: apiData['username'],
                };
            }

            if (!requesterInfo) {
                requesterInfo = {
                    id: apiData['requester'],
                    description: apiData['requester'],
                    email: apiData['requester'],
                };
            }
            const defaultDeliveryAddress = apiData['deliveryAddressMD'] ? {...apiData['deliveryAddressMD']} : {
                name: '',
                name2: '',
                houseNumber: '',
                street: '',
                postalCode: '',
                city: '',
                state: '',
                country: '',
                manuallyModified: false,
            };

            const contractId = apiData.contractID || apiData.contractId;

            const purchaseRequest = new PurchaseRequest(
                apiData.childSupplierName,
                apiData.childSupplierCode,
                new OneTimeVendor(
                    '',
                    {
                        name: '',
                        name2: '',
                        houseNumber: '',
                        street: '',
                        postalCode: '',
                        city: '',
                        state: '',
                        country: '',
                    },
                    '',
                    apiData.childSupplierCode.toString().padStart(10, '0') === OTVCode ? OneTimeVendor.supplierTypes.otv :
                        apiData.childSupplierCode.toString().padStart(10, '0') === CMVCode ? OneTimeVendor.supplierTypes.cmv :
                            apiData.childSupplierCode.toString().padStart(10, '0') === MTVCode ? OneTimeVendor.supplierTypes.mtv :
                                OneTimeVendor.supplierTypes.generic,
                ),
                new NBSValidation([], '', [], ''),
                new SBValidation([], '', ''),
                today,
                '',
                apiData.projectTitle || '',
                new WaysOfBuying(formattedWaysOfBuying.currentValue, formattedWaysOfBuying.availableOptions),
                {
                    label: spenderInfo ?
                        spenderInfo.description || userNameUppercase :
                        userNameUppercase,
                    value: userNameUppercase,
                    email: spenderInfo?.email ?? userNameUppercase,
                },
                {
                    label: requesterInfo ?
                        requesterInfo.description || requester :
                        requester,
                    value: requester,
                    email: requesterInfo?.email ?? requester,
                },
                '',
                `Please send the PDF of the PO to:\n---\nRequester: \nSpender: \nSupplier: \nOther: \n---\nProject/Order title: ${apiData.projectTitle || ''}\n\n${apiData?.comment ?? ''}`,
                apiData.commentToSupplier || '',
                new HeaderDetails(
                    Array.isArray(apiData['companyCodes']) ?
                        apiData['companyCodes'].map((companyCode) => parseLabelValue(companyCode)) :
                        [],
                    {
                        label: '',
                        value: '',
                    },
                    parseLabelValue(apiData.purchaseOrg),
                    {
                        label: '',
                        value: '',
                    },
                    {
                        label: '',
                        value: '',
                    },
                    apiData['plants'] ? parsePlants(apiData['plants']) : [],
                    apiData['plants']?.length > 0 ? parsePlants(apiData['plants'][0]) : {
                        label: '',
                        value: '',
                        addressNumber: '',
                    },
                    {
                        label: '',
                        value: '',
                    },
                    defaultDeliveryAddress,
                    deliveryDate,
                    '',
                    '',
                    {
                        label: '',
                        value: '',
                    },
                    {
                        label: '',
                        value: '',
                    },
                    {
                        label: '',
                        value: '',
                    },
                    {
                        label: '',
                        value: '',
                    },
                    {
                        data: {
                            label: '',
                            value: '',
                        }, manuallyModified: false,
                    },
                    '',
                    false,
                    false,
                    true,
                ),
                new POSendingOptions(
                    true,
                    true,
                    false,
                    '',
                    false,
                    [],
                    false,
                    false,
                    false,
                    hidePrice,
                    '',
                    false,
                ),
                new FinancialInfo(
                    false,
                    [],
                    parseAccounts(apiData.accountDetails),
                ),
                Array.isArray(apiData?.materials) ?
                    apiData?.materials.map((material, i) => {
                        return new Material(
                            i,
                            material.materialNumber + ' - ' + material.materialName.slice(0, MAX_LENGTH_OF_MATERIAL_NAME_AND_MATERIAL_DESC),
                            material.shortDescription.slice(0, MAX_LENGTH_OF_MATERIAL_NAME_AND_MATERIAL_DESC),
                            formattedWaysOfBuying.currentValue.value === 'FO' ? '1' : material.quantity?.toString() ?? '',
                            material.unitOfMeasure,
                            material.baseUnit,
                            material.unitPrice,
                            material.currencyCode,
                            material.pricePer || 1,
                            {label: '', value: ''},
                            '',
                            apiData.spendCat1Code + ' - ' + apiData.spendCat1DisplayValue,
                            material.spendCat3Code + ' - ' + material.spendCat3DisplayValue,
                            deliveryDate,
                            parseLabelValue(apiData['plants'][0]),
                            {label: '', value: ''},
                            material.deliveryAddress,
                            '',
                            false,
                            false,
                            apiData.supplierPartId,
                            '',
                            checkIfPriceDataFromAPI(material.unitPrice),
                            '',
                            '',
                            1.0,
                            material.materialType,
                            material.statusName,
                            material.pslId,
                            material.transportCostType,
                            material.transportCostConditions,
                            material.pslMaterialId,
                            material.ppmCostPlan,
                            material.taxAmount,
                            convertSourceToLabelValue(apiData.source3LettersId),
                        );
                    }) :
                    (() => {
                        return [new Material(
                            0,
                            apiData.materialNumber + ' - ' + apiData.materialName.slice(0, MAX_LENGTH_OF_MATERIAL_NAME_AND_MATERIAL_DESC),
                            apiData.shortDescription.slice(0, MAX_LENGTH_OF_MATERIAL_NAME_AND_MATERIAL_DESC),
                            formattedWaysOfBuying.currentValue.value === 'FO' ? '1' : apiData.quantity?.toString() ?? '',
                            apiData.unitOfMeasure,
                            apiData.baseUnit,
                            apiData.unitPrice,
                            apiData.currencyCode,
                            apiData.pricePer || 1,
                            {label: '', value: ''},
                            '',
                            apiData.spendCat1Code + ' - ' + apiData.spendCat1DisplayValue,
                            apiData.spendCat3Code + ' - ' + apiData.spendCat3DisplayValue,
                            deliveryDate,
                            parseLabelValue(apiData['plants'][0]),
                            {label: '', value: ''},
                            {
                                name: '',
                                name2: '',
                                houseNumber: '',
                                street: '',
                                state: '',
                                city: '',
                                postalCode: '',
                                country: '',
                                manuallyModified: false,
                            },
                            '',
                            false,
                            false,
                            apiData.supplierPartId,
                            '',
                            checkIfPriceDataFromAPI(apiData.unitPrice),
                            '',
                            '',
                            1.0,
                            apiData.type,
                            apiData.statusName || 'Blank',
                            apiData.pslId,
                            apiData.transportCostType,
                            apiData.transportCostConditions,
                            apiData.pslMaterialId,
                            apiData.ppmCostPlan,
                            apiData.taxAmount,
                            convertSourceToLabelValue(apiData.source3LettersId),
                        )];
                    })(),
                new AttachmentsInfo(
                    [],
                    [],
                    '',
                    '',
                    false,
                    '',
                    complianceIndicatorOptions[0],
                ),
                PurchaseRequest.statuses.created,
                cleanEmailList([apiData.vendorContactEmail]), // cleanEmailList checks that input is not null or is an empty string
                apiData.supplierLeadTime,
                (apiData.source === SOURCES.AGENCY_MANIA) || (apiData.childSupplierCode.toString().padStart(10, '0') === OTVCode) || (apiData.childSupplierCode.toString().padStart(10, '0') === CMVCode),
                apiData.source,
                contractId,
                '',
                apiData.sourcingEventID,
                apiData.buyer,
                '',
                uniquePrId,
                apiData.sourceFromApiKey,
                '',
                null,
                getStringValueForSourceFromApiKey(apiData.source, apiData.sourceFromApiKey),
                convertSourceToLabelValue(apiData.source3LettersId),
            );

            setPRJson(createPRJson(purchaseRequest));
        } catch (e) {
            console.error(e);
        }
    }
}

registerBrick('017d329fb0916d3c1439', ConvertAPIPayloadToPRJson);
